import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Alert } from 'antd';
import customerService from '../../services/customerService';
import { useAuth } from '../../contexts/AuthContext';
import PageShell from '../../components/common/PageShell';
import OrgProfileCard from './partials/OrgProfileCard';
import DashboardSection from './partials/DashboardSection';
import OverviewSection from './partials/OverviewSection';
import RecentInvoicesSection from './partials/RecentInvoicesSection';
import RecentEstimatesSection from './partials/RecentEstimatesSection';
import { buildDashboardInvoiceColumns, buildDashboardEstimateColumns } from './dashboardColumns';
import './DashboardPage.css';

const DashboardPage = () => {
  const navigate = useNavigate();
  const {
    user,
    organizationName,
    organizationLogo,
    organizationCover,
    organizationShowLogo,
    organizationShowCover,
    organizationTagline,
    organizationDescription,
    organizationPublicIdentifiers,
    organizationPhone,
    organizationEmail,
    organizationWebsites,
  } = useAuth();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await customerService.getDashboardStats();
      setStats(data);
    } catch (err) {
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const invoiceColumns = useMemo(() => buildDashboardInvoiceColumns(navigate), [navigate]);
  const estimateColumns = useMemo(() => buildDashboardEstimateColumns(navigate), [navigate]);

  const firstName = user?.first_name ?? 'there';
  const showCover = organizationShowCover && !!organizationCover;
  const showLogo = organizationShowLogo && !!organizationLogo;

  return (
    <PageShell className="customer-dashboard-page">
      <DashboardSection>

          <OrgProfileCard
            organizationName={organizationName}
            organizationLogo={organizationLogo}
            organizationCover={organizationCover}
            organizationTagline={organizationTagline}
            organizationDescription={organizationDescription}
            organizationPublicIdentifiers={organizationPublicIdentifiers}
            organizationPhone={organizationPhone}
            organizationEmail={organizationEmail}
            organizationWebsites={organizationWebsites}
            showCover={showCover}
            showLogo={showLogo}
            firstName={firstName}
          />
      </DashboardSection>

          {error && (
            <Alert
              type="error"
              title={error}
              showIcon
              action={<Button size="small" variant="filled" onClick={fetchStats}>Retry</Button>}
              style={{ marginBottom: 24, borderRadius: 6, marginTop: -4 }}
            />
          )}

          <DashboardSection>
            <OverviewSection loading={loading} stats={stats} />
          </DashboardSection>

          <DashboardSection>
            <RecentInvoicesSection
              loading={loading}
              stats={stats}
              navigate={navigate}
              invoiceColumns={invoiceColumns}
            />
          </DashboardSection>

          <DashboardSection>
            <RecentEstimatesSection
              loading={loading}
              stats={stats}
              navigate={navigate}
              estimateColumns={estimateColumns}
            />
          </DashboardSection>
    </PageShell>
  );
};

export default DashboardPage;
